/**
 * 「应用至整单」覆盖逻辑 —— 纯函数层。
 *
 * 两个层级、两套语义，名字像但**不可互相替代**（详见各自注释）：
 *  - `applyValuesToRows` / `getCoverByCoverMode`：**行级 · 按字段** —— 从
 *    `CooperationFormList/index.tsx` 的 `handleApplyValuesToTheWholeOrder` 下沉，
 *    折叠模式与表格模式共用（主体沿用，另有**有意**的语义修正，见其注释）。
 *
 * 【「空」的统一判据 · 三层谓词】两条 retain 路径**共用同一套**（第 6 轮统一；此前是两套口径，
 * 导致「应用至整单」与「字段应用至整单」在边界上不一致）：
 *  第 1 层 `isBlankScalar`（标量）→ 第 2 层 `hasBusinessContent`（子项）→
 *  第 3 层 `isBlankSubArray`（子数组，**唯一对外入口**，经 `getCoverByCoverMode` 生效）。
 *  语义：**「无业务内容」= 空**。各层契约见其注释。
 *  - `applySubArrayToRows`：**子数组级**（覆盖单位 = 整个子数组，无字段勾选）—— 从卡片模式
 *    KPITable / EquityFormTable 的「应用至整单」按钮（`handleApplyToTheWholeOrder`）提炼，
 *    表格模式三类区块（指标 / 必要权益 / 期望权益）共用；支持 `retain`（按子项整份）/
 *    `overwrite`（整份覆盖），见其注释。
 */
import type { DemandCooperatePropertyValueDto } from '@mpc/common/src/services/api';

export type CoverMode = 'retain' | 'overwrite';

export interface ApplyValuesToRowsOptions {
  // 覆盖模式：保留原值或者覆盖原值
  coverMode: CoverMode;
  // 覆盖字段（如 'property_values.DCP0058' / 'kpi_values' / 'remark' / '__SELECT_ALL__' 等）
  coverKeys: string[];
}

/** 标量空值：`0` / `false` / 空白串都**不算**空（与业务侧 `isEmptyFieldValue` 口径一致） */
const isBlankScalar = (value: any): boolean =>
  value === undefined || value === null || value === '';

// ── 「空」的**统一判据**：三层谓词（第 1 层标量 / 第 2 层子项 / 第 3 层子数组）──
//   第 1 层 `isBlankScalar`、第 2 层 `hasBusinessContent`、第 3 层 `isBlankSubArray`；
//   **唯一对外入口是 `isBlankSubArray`**（传入非数组时自动降级到第 1 层）。
//   「应用至整单」（子数组级）与「字段应用至整单」（行级按字段）**共用这一套**。
//   ⚠️ 历史上这里另有一个 `isBlankArray`（只认 `[]` / 全空标量数组 / `[{}]` 特例），已被
//   第 3 层取代 —— 它认不出「仅含默认值的空行」，与「应用至整单」的口径不一致。
//   各层定义见 `isBlankSubArray` 上方的注释块。

/**
 * 按 coverMode 决定最终覆盖值 —— **所有 retain 判空的唯一出口**。
 *
 * - `overwrite`：恒用 `coverValue`（**不判空**，与判据无关）
 * - `retain`：原值「空」则用 `coverValue`，否则保留原值。
 *   「空」由**统一判据** `isBlankSubArray(originalValue)`（不带 subKey）给出 ——
 *   对标量降级到 `isBlankScalar`、对数组走子项业务内容判定（详见其上方注释块）。
 *
 * `isEmpty` 形参：调用方**按具体 subKey 覆盖判据**时的入口 —— 仅子数组字段
 *（`kpi_values` / `necessary_rights` / `expect_rights`）需要传：它们要按**业务字段白名单**判，
 * 无 subKey 的通用判定认不出「仅含默认值的空行」。**与内置判据取或**。
 *
 * ⚠️ 除「子数组字段需带 subKey」这一种情形外，**不要**再给本函数加第三套判空 ——
 * 消除多套判空正是本函数存在的意义。
 */
export const getCoverByCoverMode = (
  coverMode: CoverMode,
  coverValue: any,
  originalValue: any,
  isEmpty = false,
): any => {
  if (coverMode === 'overwrite') {
    return coverValue;
  }
  if (isEmpty || isBlankSubArray(originalValue)) {
    return coverValue;
  }
  return originalValue;
};

/**
 * 子数组子项的**业务字段**白名单（按 subKey 分派），以及各字段的「默认值」。
 *
 * 判定规则（`hasBusinessContent`）：
 *  - 字段值**非空**（`undefined` / `null` / `''` 为空，见 `isBlankScalar`）
 *    **且不等于该字段的默认值** ⇒ 该字段「有内容」；
 *  - 子项**任一**业务字段有内容 ⇒ 该子项「有内容」；
 *  - 元数据字段（`id` / `index_id` / `kpi_schema` / `label_obj` 等）**不参与**判定。
 *
 * ⚠️ 用「字段 + 默认值」而不是「所有 `false`/`0` 都算空」：`expect_value: 0` 是**有意义的
 * 预期值**，必须算内容（与 `isBlankScalar` 的既有口径一致）；只有像 `accept_flag: false`、
 * `custom: 0` 这种**等于默认值**的才不算。
 */
const SUB_ITEM_CONTENT_FIELDS: Record<string, { key: string; defaultValue?: unknown }[]> = {
  // 考核指标：`kpiSubArraySpec.makeDefault` 产出 `{id,kpi_code:'',expect_value:'',
  // accept_flag:false,custom:0}` ⇒ 全部字段走默认/空 ⇒ 判为「空」（这正是本判据要覆盖的形态）
  kpi_values: [
    { key: 'kpi_code' },
    { key: 'expect_value' },
    { key: 'accept_flag', defaultValue: false },
    { key: 'accept_remark' },
    { key: 'show_for_out', defaultValue: false },
    { key: 'actual_value' },
    { key: 'result' },
    { key: 'custom', defaultValue: 0 },
  ],
  // 权益：「权益库标准权益」与「本单实际权益内容」任一非空即有内容。
  // ⚠️ `id` 是兜底生成的元数据、`rights_code` 与前两者同源同生共死，均不参与。
  necessary_rights: [{ key: 'content' }, { key: 'actual_content' }],
  expect_rights: [{ key: 'content' }, { key: 'actual_content' }],
};

/**
 * 【第 2 层 · 子项】单个子项是否有**业务内容**。
 *
 * - 标量（`undefined` / `null` / `''`）⇒ 无内容（降级到第 1 层）；
 * - **已知 subKey**（有白名单）⇒ 按 `SUB_ITEM_CONTENT_FIELDS`：任一业务字段非空
 *   **且不等于其默认值** ⇒ 有内容；
 * - **未知 subKey / 不传 subKey**（字段级路径）⇒ 通用对象判据：**所有自有字段都「空」才算无内容**
 *   （`false` / `0` 仍算内容，与第 1 层一致）。
 */
const hasBusinessContent = (item: any, subKey?: string): boolean => {
  if (isBlankScalar(item)) return false;
  if (typeof item !== 'object') return true;
  const fields = subKey ? SUB_ITEM_CONTENT_FIELDS[subKey] : undefined;
  if (fields) {
    return fields.some(({ key, defaultValue }) => {
      const value = item[key];
      if (isBlankScalar(value)) return false;
      return defaultValue === undefined ? true : value !== defaultValue;
    });
  }
  // 无白名单：对象所有自有字段都空 ⇒ 无内容。
  // ⚠️ 这里**不递归**判数组 / 嵌套对象（`isBlankScalar([]) === false` ⇒ 非空数组字段算内容），
  //    方向是保守的（宁可判「有内容」而少覆盖）。
  return Object.keys(item).some((k) => !isBlankScalar(item[k]));
};

/**
 * 【第 3 层 · 子数组】值是否「无任何业务内容」——
 * 「应用至整单」（子数组级）与「字段应用至整单」（行级按字段）**共用的唯一判空入口**
 *（`getCoverByCoverMode` 内置调用它）。
 *
 * - **非数组**（标量）⇒ 降级到第 1 层 `isBlankScalar`；
 * - **数组** ⇒ 空数组，或**每个子项都无业务内容**（逐项走第 2 层）。
 *
 * `subKey`：传了才启用该子数组的**业务字段白名单**（第 2 层）；不传走通用对象判据。
 * ⇒ 子数组字段（`kpi_values` / `necessary_rights` / `expect_rights`）由调用方**显式带 subKey**；
 * 字段级路径（`property_values` 的值、`remark` 等）不带。
 *
 * ⚠️ 「填过又被清空」在 DTO 层**不是 `[]`** —— `formValueToCooperationValue` 把单值也包成
 * `[value]`，故 input/text 清空后留下 `['']`、select/dropdown 清空后留下 `[undefined]`
 * （allowClear → undefined）。第 1 层已覆盖这两形态，`length === 0` 只是其中一种。
 *
 * ⚠️ **未知 subKey 不再「一律判有内容」**（第 5 轮的保守策略已撤销，以换取两入口口径统一）：
 * 未知子数组走通用对象判据。**新增子数组时必须同步补 `SUB_ITEM_CONTENT_FIELDS` 白名单**，
 * 否则若其子项带元数据字段（如 `id`），空行会被通用判据判成「有内容」而漏填。
 */
const isBlankSubArray = (value: any, subKey?: string): boolean => {
  if (!Array.isArray(value)) return isBlankScalar(value);
  if (value.length === 0) return true;
  return value.every((item) => !hasBusinessContent(item, subKey));
};

/**
 * 以 rows[sourceIndex] 为源，按 coverMode/coverKeys 覆盖其余行的
 * property_values（按 property_code 精确匹配）/ kpi_values / necessary_rights / expect_rights /
 * remark 等字段。
 *
 * ⚠️ 主体沿用原 `handleApplyValuesToTheWholeOrder`，包含其既有实现细节：reduce 累加器以
 * `item`（而非新对象）为初值 —— 对非覆盖字段直接复用原行引用、对被覆盖字段做原地赋值后返回。
 * 这一「原地改写入参」是刻意保留的（改动会影响 memo 链的引用比较，属单独的行为变更）。
 *
 * 与原实现**有意不同**的三处（均为语义修正，不是重构走样）：
 *  ① `getCoverByCoverMode` 的判空覆盖「填过又被清空」的数组形态（`[]` / `['']` / `[undefined]`）；
 *  ② 目标行缺某 property_code 的补全分支取源行真实值，不再写死 `[]`；
 *  ③ **判空统一到三层谓词**（第 6 轮）：子数组字段带 `subKey` 走业务字段白名单；字段级
 *    （`property_values` 的值等）走同一谓词的通用分支 —— 两入口口径自此一致。
 *    ⚠️ 只经 `getCoverByCoverMode` 的内置判据 / `isEmpty` 形参生效，**`overwrite` 路径完全不受影响**。
 */
export const applyValuesToRows = <T extends Record<string, any>>(
  rows: T[],
  sourceIndex: number,
  { coverMode, coverKeys }: ApplyValuesToRowsOptions,
): T[] => {
  // 获取当前合作内容的值，基于coverMode 重置整个合作内容list
  const currentValue = rows[sourceIndex];
  // property_values注意要基于property_values.property_code 取值进行覆盖
  const coverMap = coverKeys.reduce((result: Record<string, boolean>, key) => {
    result[key] = true;
    return result;
  }, {});

  return rows.map((item: Record<string, any>, index) => {
    if (index === sourceIndex) {
      return item;
    }
    return Object.keys(currentValue).reduce((result: Record<string, any>, key) => {
      if (!coverMap[key] && key !== 'property_values') {
        result[key] = item[key];
        return result;
      }
      switch (key) {
        case 'property_values':
          result['property_values'] = item['property_values'].map(
            (propertyValue: DemandCooperatePropertyValueDto) => {
              if (coverMap[`property_values.${propertyValue.property_code}`]) {
                const coverValue = currentValue['property_values'].find(
                  (v: DemandCooperatePropertyValueDto) => v.property_code === propertyValue.property_code,
                )?.value;
                return {
                  ...propertyValue,
                  value: coverMap[`property_values.${propertyValue.property_code}`]
                    ? getCoverByCoverMode(coverMode, coverValue, propertyValue.value)
                    : propertyValue.value,
                };
              } else {
                return propertyValue;
              }
            },
          );
          // 二询草稿单场景下可能存在
          currentValue['property_values'].forEach((coverPropertyValue: DemandCooperatePropertyValueDto) => {
            // 如果result['property_values']中没有该property_code，则添加
            if (
              !result['property_values'].some(
                (v: DemandCooperatePropertyValueDto) => v.property_code === coverPropertyValue.property_code,
              )
            ) {
              result['property_values'].push({
                ...coverPropertyValue,
                // 目标行**没有**这一项 ⇒ 它的原值不存在，按「空」处理，两种模式结果一致：
                // overwrite 恒用源值；retain 也是「原值为空 → 写入源值」。
                // ⚠️ 原实现在此处传的 coverValue 是 `[]`（而非源行真实值），于是 overwrite
                // 会把这个字段**写成空数组** —— 与「覆盖原值」的语义正好相反。
                // 故这里不做转换，直接用展开进来的源行 value（仅未选中字段才置空）。
                value: coverMap[`property_values.${coverPropertyValue.property_code}`]
                  ? coverPropertyValue.value
                  : [],
              });
            }
          });
          break;
        // 子数组字段（考核指标 / 必要权益 / 期望权益）判空统一走 `isBlankSubArray`：
        // **子项只要没有任何业务内容就视为空**，应被填入（与卡片模式口径一致）。
        // 取代原先 kpi 专用的硬编码 JSON 字符串比较（`'[{"accept_flag":false,"custom":0}]'`，
        // 脆弱且与子数组级口径不一致）。
        // ⚠️ 该判据只作用于 `isEmpty` 形参 ⇒ `overwrite` 路径完全不受影响。
        case 'kpi_values':
        case 'necessary_rights':
        case 'expect_rights': {
          result[key] = getCoverByCoverMode(coverMode, currentValue[key], item[key], isBlankSubArray(item[key], key));
          break;
        }
        default:
          result[key] = getCoverByCoverMode(coverMode, currentValue[key], item[key]);
          break;
      }
      return result;
    }, item);
  }) as T[];
};

/**
 * 子数组级「应用至整单」：把 `rows[sourceIndex]` 的 `subKey` 子数组按 `coverMode` 写入所有行。
 *
 * 【两种模式】
 *  - `overwrite`（覆盖原值）：**整份覆盖** —— 逐字对齐卡片模式
 *    `CooperationFormList.handleApplyToTheWholeOrder` 的 `{...item, [key]: value}`：
 *    不 merge / 不去重 / 不追加。源行自身也在 `map` 范围内，被写成同一值（等价 no-op）。
 *  - `retain`（保留原值 · **按子项整份**）：**仅当目标行该子数组「无任何业务内容」时才整份填入**；
 *    目标行有内容则**完全不动**。判空走 `isBlankSubArray`（按 subKey 的业务字段白名单，
 *    见 `SUB_ITEM_CONTENT_FIELDS`）—— 空数组、子项全为空白、以及**仅含默认值的空行**
 *    （如 kpi 的 `{kpi_code:'',expect_value:'',accept_flag:false,custom:0}`、
 *    权益的 `{content:'',actual_content:''}`）都算「空」，会被填入。
 *
 * 【⚠️ retain 是「按子项整份」，**不是**「按字段逐列填空」】举例：目标行已有 2 个**有内容**的
 * 指标、源行 3 个 —— 目标行**完全不动**（既不逐字段补空，也不追加第 3 个）。
 * 若将来产品要「补齐/追加」，那是 merge 语义，属**另一需求**。
 *
 * 【⚠️ 与 `applyValuesToRows` 的区别】后者是**行级 · 按字段**（勾选 `coverKeys` +
 * retain/overwrite，可跨多个字段）；本函数是**子数组级**，覆盖单位就是「整个子数组」，
 * **没有**字段级勾选。两者名字相近，改动前先确认调用方要的是哪一种。
 * 两者的**子数组判空口径已统一**为 `isBlankSubArray`（见该谓词注释）。
 *
 * 【retain 顺带消除的一个风险】`overwrite` 下源行为空会把整单清空（见下）；`retain` 下
 * 源行为空时目标行非空则保留原值、目标行为空则写入空数组（本就是空）—— **不会误清空整单**。
 *
 * 【为何可直接共享同一份数组引用】引擎写侧全部是不可变写回（`value.map` + `{...it, ...p}`；
 * 子数组经 `spec.setItems` 返回新数组），任一行后续编辑只替换该行自己的引用，不波及其余行。
 * 故无需逐行深拷贝 —— 与卡片模式共享引用的行为保持一致。
 *
 * 【⚠️ `overwrite` 不判空】源行该子数组为空（`[]` / 缺字段）时，整单会被一并写成空数组 ——
 * 这是「覆盖」语义的固有后果，与卡片模式一致（卡片模式同样不判空）。
 *
 * @param coverMode 默认 `'overwrite'` —— 保持既有调用点行为不变；新调用方应**显式传入**。
 */
export const applySubArrayToRows = <T extends Record<string, any>>(
  rows: T[],
  sourceIndex: number,
  subKey: string,
  coverMode: CoverMode = 'overwrite',
): T[] => {
  const sourceItems = rows[sourceIndex]?.[subKey] ?? [];
  return rows.map((item) => ({
    ...item,
    // 判空走 `isBlankSubArray`：**子项只要没有任何业务内容就视为空**，应被填入。
    // 该判据只作用于 `isEmpty` 形参 ⇒ `overwrite` 路径完全不受影响。
    [subKey]: getCoverByCoverMode(coverMode, sourceItems, item?.[subKey], isBlankSubArray(item?.[subKey], subKey)),
  }));
};
